import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { MainMedia } from '@/core/components/MainMedia';
import { StoppedPageComponent } from '@/core/components/StoppedPageComponent';
import {
  ErrorsFields,
  FormState,
  SignupDetails,
  SignupFormPayload,
} from '@/core/interfaces/common';

import { SignupFormContainerRedux } from '@/features/NewForm/SignupFormContainerRedux';
import { ShowMoreHtml } from '@/features/FormsDesignV4/components/ShowMoreHtml';
import { ShowMoreText } from '@/features/FormsDesignV4/components/ShowMoreText';
import { ThankYouWrapper } from '@/features/FormsDesignV4/components/ThankYouWrapper';

import {
  handleCreateCustomFieldsParams,
  scrollToTop,
} from '@/utils/helpers';
import {
  ConversionDetails,
  googleAnalyticsPageview,
  initGoogleTagManager,
  metaPixelTrackLead,
  trackCustomerConversion,
} from '@/utils/analytics';
import {
  getAnalyticsSessionId,
  trackFormSubmit,
} from '@/utils/beacon';
import { request } from '@/utils/request';
import { getSubmitOutcome } from '@/utils/submitOutcome';
import { useResponsiveMaxLength } from '@/utils/hooks/useResponsiveMaxLength';
import { useFormValidationFocus } from '@/utils/useFormValidationFocus';

import {
  GeneralErrorMessage,
  LogoLink,
  TextAreaDescription,
  ThankYouMessage,
} from '@/themes/globalStyles';

import {
  Form,
  WhyTakeActionSection,
} from './ContactFormV4.styles';
import {
  FormSection,
  FormWrapper,
  Header,
  LandingPageWrapper,
  Logo,
  LogoWrapper, StyledTextDescription,
  StyledWhyTakeActionWrapper, Title,
} from '../FormsDesignV4/FormsDesignV4.styles';
import { LandingPageFooter } from '../FormsDesignV4/components/LandingPageFooter';

export interface SubmitResponse {
  message?: string;
  status?: string;
}

export interface SignupFormPageProps {
  signupDetails: SignupDetails;
}

const defaultBackgroundColor = '#2979FF';

export const ContactFormV4 = ({
  signupDetails,
}: SignupFormPageProps) => {
  const router = useRouter();
  const {
    uuid,
    debugThankYou,
    demoLogo,
  } = router.query;

  const {
    submitButtonText,
    facebookPixelEnabled,
    facebookPixelId,
    headlineConfig,
    googleAnalyticsEnabled,
    googleAnalyticsId,
    nextSteps,
    signupPageImage,
    logo,
    optIn,
    schema,
    status,
    organization,
    socialShareButtonEnabled,
    thankYouMessage,
  } = signupDetails;

  const {
    youtubeId,
    vimeoCode,
  } = headlineConfig;

  const [
    formState,
    setFormState,
  ] = useState<FormState>({
    customFields: [],
    email: '',
    firstName: '',
    isOptInEmail: true,
    isOptInText: true,
    lastName: '',
    phoneNumber: '',
    prefix: '',
    streetAddress: '',
    zipCode: '',
  });
  const [
    isActionTaken,
    setIsActionTaken,
  ] = useState(debugThankYou === '1');
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<ErrorsFields>({});
  const maxLength = useResponsiveMaxLength();
  const formRef = useRef<HTMLFormElement>(null);

  useFormValidationFocus(formRef);

  useEffect(() => {
    const formCustomFields = signupDetails.customFields.map(customField => ({
      ...customField,
      value: '',
    }));

    setFormState(prevValues => ({
      ...prevValues,
      customFields: formCustomFields,
    }));
  }, [signupDetails]);

  useEffect(() => {
    if (facebookPixelId && facebookPixelEnabled) {
      import('react-facebook-pixel')
        .then(module => module.default)
        .then(ReactPixel => {
          ReactPixel.init(facebookPixelId, undefined, {
            autoConfig: true,
            debug: false,
          });
          ReactPixel.pageView(); // For tracking page view
        });
    }
  }, [
    facebookPixelId,
    facebookPixelEnabled,
  ]);

  useEffect(() => {
    if (googleAnalyticsId) {
      if (googleAnalyticsId.startsWith('GTM-')) {
        initGoogleTagManager(googleAnalyticsId);
      } else {
        googleAnalyticsPageview(googleAnalyticsId);
      }
    }
  }, [googleAnalyticsId]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsPending(true);
    const {
      customFields,
      ...restFormState
    } = formState;

    const params: SignupFormPayload = {
      ...restFormState,
      sessionId: getAnalyticsSessionId(),
      ...(customFields ?
        { customFields: handleCreateCustomFieldsParams(customFields) } :
        {}),
    };

    return request
      .post<SubmitResponse>(`/public/signup/${uuid}/`, params)
      .then(({ data }) => {
        if (data.status === 'error') {
          setError({
            submitError: data.message,
          });
        } else {
          trackFormSubmit({
            organization: organization.uuid,
            version: 'v4',
          });
        }
        setIsActionTaken(true);

        const conversion: ConversionDetails = {
          actionType: 'signup',
          formType: 'signup_form',
          outcome: getSubmitOutcome(data.status),
        };

        metaPixelTrackLead(facebookPixelId, facebookPixelEnabled, conversion);
        trackCustomerConversion(googleAnalyticsId, googleAnalyticsEnabled, conversion);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        if (data?.status === 'error') {
          setError({
            generalError: data.message,
          });
        } else {
          setError(data);
        }
      })
      .finally(() => {
        setIsPending(false);
        scrollToTop();
      });
  };

  const renderActionTaken = () => (
    <ThankYouWrapper
      buttonColor={headlineConfig?.buttonColor || '#2979FF'}
      borderColor={pageBackgroundColor}
      isFullScreen
      nextSteps={nextSteps}
      name={error?.submitError || 'Submission complete!'}
      socialShareButtonEnabled={socialShareButtonEnabled}
      textcolor={headlineConfig?.textColor || '#000'}
      uuid={uuid}
    >
      {!error?.submitError && thankYouMessage && (
        <ThankYouMessage
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: thankYouMessage,
          }}
        />
      )}
    </ThankYouWrapper>
  );

  if (status === 'stopped') {
    return (
      <StoppedPageComponent
        backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
        textColor={headlineConfig?.textColor || '#000'}
        title="This Signup has been stopped."
      />
    );
  }

  const websiteLink = organization.websiteUrl?.startsWith('http://') || organization.websiteUrl?.startsWith('https://') ? organization.websiteUrl : `https://${organization.websiteUrl}`;
  const pageBackgroundColor = headlineConfig?.backgroundColor || defaultBackgroundColor;

  // Demo logo functionality allows testing the design with placeholder images
  // If demoLogo is present in the URL query params, it will be used instead of the actual logo
  // The demo logo should be placed in the /public/demo-data/ directory
  const displayLogo = demoLogo ? `/demo-data/${demoLogo}` : logo;

  const headerSection = (
    <Header
      backgroundcolor={pageBackgroundColor}
      isActionTaken={isActionTaken}
      textcolor={headlineConfig?.textColor || '#000'}
    >
      <LogoWrapper>
        <LogoLink
          aria-label={organization.websiteUrl ? `${organization.name} website` : undefined}
          href={organization.websiteUrl ? websiteLink : undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {displayLogo ?
            (
              <Logo
                alt=""
                role="presentation"
                src={displayLogo}
                title={`${organization.name} logo`}
              />
            ) :
            organization.name
          }
        </LogoLink>
      </LogoWrapper>
    </Header>
  );

  const footerSection = (
    <LandingPageFooter
      backgroundColor={pageBackgroundColor}
      organization={organization}
      shareMessage="Share this page on your social media channels"
      shareTitle={headlineConfig?.headlineText}
      socialShareButtonEnabled={socialShareButtonEnabled}
      textColor={headlineConfig?.textColor}
      uuid={uuid}
    />
  );

  return (
    <LandingPageWrapper
      backgroundcolor={isActionTaken ? pageBackgroundColor : 'white'}
      className={schema?.cssClass}
      isActionTaken={isActionTaken}
    >
      {headerSection}
      {!isActionTaken && (
        <>
          <FormWrapper>
            <WhyTakeActionSection>
              <StyledWhyTakeActionWrapper>
                <MainMedia
                  vimeoCode={vimeoCode}
                  youtubeId={youtubeId}
                  mainImage={signupPageImage}
                />
                <Title>
                  {headlineConfig?.headlineText}
                </Title>
                {headlineConfig?.descriptionBody ?
                  (
                    <ShowMoreHtml
                      htmlWrapper={StyledTextDescription}
                      html={headlineConfig?.descriptionBody}
                      maxLength={maxLength}
                    />
                  ) :
                  (
                    <ShowMoreText
                      textWrapper={TextAreaDescription}
                      text={headlineConfig?.textArea}
                      maxLength={maxLength}
                    />
                  )}
              </StyledWhyTakeActionWrapper>
            </WhyTakeActionSection>
            <FormSection backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}>
              {error?.generalError && (
                <GeneralErrorMessage>{error?.generalError}</GeneralErrorMessage>
              )}
              <Form>
                <form
                  autoComplete="off"
                  onSubmit={handleSubmit}
                  ref={formRef}
                >
                  <SignupFormContainerRedux
                    backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
                    buttonColor={headlineConfig?.buttonColor || '#2979FF'}
                    error={error}
                    formState={formState}
                    isPending={isPending}
                    optIn={optIn}
                    organizationName={organization.name}
                    setError={setError}
                    setFormState={setFormState}
                    submitButtonText={submitButtonText}
                    textColor={headlineConfig?.textColor || '#000'}
                  />
                </form>
              </Form>
            </FormSection>
          </FormWrapper>
          {footerSection}
        </>
      )}
      {isActionTaken && renderActionTaken()}
    </LandingPageWrapper>
  );
};
